import { useMemo, useState } from 'react';
import {
  Chip,
  Box,
  Typography,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import AppLayout from '../components/layout/AppLayout';
import CopyIconButton from '../components/ui/CopyIconButton';
import AddOperatorModal from '../components/operators/AddOperatorModal';
import {
  useOperators,
  useUpdateOperator,
  useRefreshOperators,
} from '../hooks/queries/useOperators';
import { type Operator } from '../services/operators-api';
import { getOperatorSpecialtyColor } from '../utils/statusColors';
import {
  OPERATOR_SPECIALTY,
  OPERATOR_SPECIALTY_OPTIONS,
} from '../types/db-types';
import { STATE_NAMES, US_STATES } from '../constants/states';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { tableHeaderBackground } from '../theme/palette';

export default function OperatorsPage() {
  const { data: operators = [], isLoading: loading, error } = useOperators();
  const updateOperatorMutation = useUpdateOperator();
  const refreshOperators = useRefreshOperators();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const columns = useMemo<MRT_ColumnDef<Operator>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        filterVariant: 'text',
        enableEditing: true,
        size: 200,
        Cell: ({ cell }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover .copy-icon': { opacity: 1 },
            }}
          >
            <span>{cell.getValue<string>() || '-'}</span>
            <CopyIconButton
              text={cell.getValue<string>() || ''}
              label="Name"
              className="copy-icon"
            />
          </Box>
        ),
      },
      {
        accessorKey: 'contactName',
        header: 'Contact Name',
        filterVariant: 'text',
        enableEditing: true,
        size: 200,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        filterVariant: 'text',
        enableEditing: true,
        size: 250,
        Cell: ({ cell }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover .copy-icon': { opacity: 1 },
            }}
          >
            <span>{cell.getValue<string>() || '-'}</span>
            <CopyIconButton
              text={cell.getValue<string>() || ''}
              label="Email"
              className="copy-icon"
            />
          </Box>
        ),
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        filterVariant: 'text',
        enableEditing: true,
        size: 150,
        Cell: ({ cell }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover .copy-icon': { opacity: 1 },
            }}
          >
            <span>{cell.getValue<string>() || '-'}</span>
            <CopyIconButton
              text={cell.getValue<string>() || ''}
              label="Phone"
              className="copy-icon"
            />
          </Box>
        ),
      },
      {
        accessorKey: 'website',
        header: 'Website',
        filterVariant: 'text',
        enableEditing: true,
        size: 200,
        Cell: ({ cell }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '&:hover .copy-icon': { opacity: 1 },
            }}
          >
            <span>{cell.getValue<string>() || '-'}</span>
            <CopyIconButton
              text={cell.getValue<string>() || ''}
              label="Website"
              className="copy-icon"
            />
          </Box>
        ),
      },
      {
        accessorKey: 'specialty',
        header: 'Specialties',
        filterVariant: 'multi-select',
        filterSelectOptions: OPERATOR_SPECIALTY_OPTIONS.map(opt => ({
          label: opt.replace(/_/g, ' ').toLowerCase(),
          value: opt,
        })),
        enableEditing: true,
        editVariant: 'select',
        editSelectOptions: OPERATOR_SPECIALTY_OPTIONS.map(opt => ({
          label: opt.replace(/_/g, ' ').toLowerCase(),
          value: opt,
        })),
        muiFilterSelectProps: {
          renderValue: (selected: string[]) => {
            if (Array.isArray(selected)) {
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(value => {
                    const colors = getOperatorSpecialtyColor(
                      value as OPERATOR_SPECIALTY
                    );
                    return (
                      <Chip
                        key={value}
                        label={value.replace(/_/g, ' ').toLowerCase()}
                        size="small"
                        sx={{
                          backgroundColor: colors.backgroundColor,
                          color: colors.color,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                        }}
                      />
                    );
                  })}
                </Box>
              );
            }
            return selected;
          },
        },
        muiEditTextFieldProps: ({ cell }) => ({
          select: true,
          value: (() => {
            const cellValue = cell.getValue();
            // Handle array values for specialty field
            if (Array.isArray(cellValue) && cellValue.length > 0) {
              return cellValue[0]; // Show first specialty for editing
            }
            return cellValue || '';
          })(),
          onChange: async event => {
            const newValue = event.target.value;
            const rowId = cell.row.original.id;
            const columnId = cell.column.id;

            if (rowId && columnId && newValue !== cell.getValue()) {
              // For specialty, we need to pass an array since it's an array field in the database
              const valueToUpdate =
                columnId === 'specialty' ? [newValue] : newValue;
              updateOperatorMutation.mutate({
                id: rowId,
                field: columnId,
                value: valueToUpdate,
              });
            }
          },
          children: OPERATOR_SPECIALTY_OPTIONS.map(specialty => {
            const colors = getOperatorSpecialtyColor(specialty);
            return (
              <MenuItem key={specialty} value={specialty}>
                <Chip
                  label={specialty.replace(/_/g, ' ').toLowerCase()}
                  size="small"
                  sx={{
                    backgroundColor: colors.backgroundColor,
                    color: colors.color,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                  }}
                />
              </MenuItem>
            );
          }),
        }),
        size: 200,
        Cell: ({ cell }) => {
          const specialties = cell.getValue<OPERATOR_SPECIALTY[]>();
          if (!specialties || !Array.isArray(specialties)) return '-';

          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {specialties.map((specialty, index) => {
                const colors = getOperatorSpecialtyColor(specialty);
                return (
                  <Chip
                    key={index}
                    label={specialty.replace(/_/g, ' ').toLowerCase()}
                    size="small"
                    sx={{
                      backgroundColor: colors.backgroundColor,
                      color: colors.color,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                    }}
                  />
                );
              })}
            </div>
          );
        },
      },
      {
        accessorKey: 'description',
        header: 'Description',
        filterVariant: 'text',
        enableEditing: true,
        size: 200,
      },
      {
        accessorKey: 'businessType',
        header: 'Business Type',
        filterVariant: 'text',
        enableEditing: true,
        size: 150,
      },
      {
        accessorKey: 'address',
        header: 'Address',
        filterVariant: 'text',
        enableEditing: true,
        size: 200,
      },
      {
        accessorKey: 'city',
        header: 'City',
        filterVariant: 'text',
        enableEditing: true,
        size: 120,
      },
      {
        accessorKey: 'state',
        header: 'State',
        filterVariant: 'select',
        filterSelectOptions: US_STATES.map(stateCode => ({
          label: `${stateCode} - ${STATE_NAMES[stateCode]}`,
          value: stateCode,
        })),
        editVariant: 'select',
        editSelectOptions: US_STATES.map(stateCode => ({
          label: `${stateCode} - ${STATE_NAMES[stateCode]}`,
          value: stateCode,
        })),
        muiEditTextFieldProps: ({ cell }) => ({
          select: true,
          value: cell.getValue() || '',
          onChange: async event => {
            const newValue = event.target.value;
            const rowId = cell.row.original.id;
            const columnId = cell.column.id;
            if (rowId && columnId && newValue !== cell.getValue()) {
              updateOperatorMutation.mutate({
                id: rowId,
                field: columnId,
                value: newValue,
              });
            }
          },
          children: US_STATES.map(stateCode => (
            <MenuItem key={stateCode} value={stateCode}>
              {stateCode} - {STATE_NAMES[stateCode]}
            </MenuItem>
          )),
        }),
        enableEditing: true,
        size: 120,
      },
      {
        accessorKey: 'zipCode',
        header: 'Zip Code',
        filterVariant: 'text',
        enableEditing: true,
        size: 100,
      },
      {
        accessorKey: 'country',
        header: 'Country',
        filterVariant: 'text',
        enableEditing: true,
        size: 120,
      },
      {
        accessorKey: 'yearsInBusiness',
        header: 'Years in Business',
        filterVariant: 'range',
        enableEditing: true,
        size: 150,
      },
      {
        accessorKey: 'employeeCount',
        header: 'Employee Count',
        filterVariant: 'range',
        enableEditing: true,
        size: 150,
      },
      {
        accessorFn: row => (row.createdAt ? new Date(row.createdAt) : null),
        id: 'createdAt',
        header: 'Created',
        filterVariant: 'date-range',
        enableEditing: false,
        size: 120,
        Cell: ({ cell }) => {
          const date = cell.getValue<Date | null>();
          return date
            ? date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : '-';
        },
      },
      {
        accessorFn: row => (row.updatedAt ? new Date(row.updatedAt) : null),
        id: 'updatedAt',
        header: 'Updated',
        filterVariant: 'date-range',
        enableEditing: false,
        size: 120,
        Cell: ({ cell }) => {
          const date = cell.getValue<Date | null>();
          return date
            ? date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : '-';
        },
      },
    ],
    [updateOperatorMutation]
  );

  const table = useMaterialReactTable({
    columns,
    data: operators,
    initialState: {
      showColumnFilters: false,
      columnVisibility: {
        businessType: false,
        address: false,
        city: false,
        state: false,
        zipCode: false,
        country: false,
        yearsInBusiness: false,
        employeeCount: false,
      },
    },
    enableEditing: true,
    editDisplayMode: 'cell',
    enableTopToolbar: true,
    renderTopToolbarCustomActions: () => {
      const handleCreateNewOperator = () => {
        setIsAddModalOpen(true);
      };
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ p: 2 }}>
            Operators
          </Typography>
          <Tooltip title="Create New Operator">
            <IconButton onClick={handleCreateNewOperator}>
              <AddBoxIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    enableClickToCopy: false,
    muiEditTextFieldProps: ({ cell }) => ({
      variant: 'standard',
      onBlur: async event => {
        const newValue = event.target.value;
        const rowId = cell.row.original.id;
        const columnId = cell.column.id;

        if (rowId && columnId && newValue !== cell.getValue()) {
          updateOperatorMutation.mutate({
            id: rowId,
            field: columnId,
            value: newValue,
          });
        }
      },
    }),
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minHeight: 0,
        '& .MuiTableCell-head': {
          backgroundColor: tableHeaderBackground,
          color: 'var(--mui-palette-primary-contrastText)',
          fontWeight: 600,
        },
      },
    },
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
        },
        '&:nth-of-type(even)': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      },
    },
  });

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          Loading...
        </Box>
      </AppLayout>
    );
  }
  if (error) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            color: 'error.main',
            textAlign: 'center',
          }}
        >
          {error instanceof Error ? error.message : 'Failed to fetch operators'}
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <MaterialReactTable table={table} />
          </Box>
          <datalist id="states-datalist-operators">
            {US_STATES.map(stateCode => (
              <option key={stateCode} value={stateCode}>
                {STATE_NAMES[stateCode]}
              </option>
            ))}
          </datalist>
        </LocalizationProvider>
      </Box>
      <AddOperatorModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onOperatorAdded={() => {
          refreshOperators();
          setIsAddModalOpen(false);
        }}
      />
    </AppLayout>
  );
}
